import type {
  SimulatorArea,
  SimulatorElementValue,
  SimulatorField,
} from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import { useCallback } from 'react'
import { FileTypeEnum } from 'utils'

import { useFilePickerModal } from 'components/pages/FinderPage'

import { createNewItem } from './helper'
import type { SimulatorState } from './simulatorTypes'

interface IUseSimulatorActionsParams {
  activeItem: SimulatorElementValue['items'][number]
  areas: SimulatorArea[]
  fields: SimulatorField[]
  selectedArea: SimulatorArea | undefined
  selectedAreaId: string | null
  handleSelectArea: (id: string | null) => void
  clearFieldSelection: () => void
  state: SimulatorState | undefined
  value: SimulatorElementValue
  onChange: ((value: SimulatorElementValue) => void) | undefined
  updateAreaInValue: (area: SimulatorArea) => void
}

export const useSimulatorActions = ({
  activeItem,
  areas,
  fields,
  selectedArea,
  selectedAreaId,
  handleSelectArea,
  clearFieldSelection,
  state,
  value,
  onChange,
  updateAreaInValue,
}: IUseSimulatorActionsParams) => {
  const filePickModal = useFilePickerModal({
    defaultContentType: FileTypeEnum.IMAGE,
  })

  const onAddField = useCallback(() => {
    const newField = {
      id: generateId(),
      x: 10,
      y: 10,
      width: 20,
      height: 5,
      value: '',
    }
    const updatedItems = value.items.map((item) => ({
      ...item,
      fields: item.value === activeItem?.value ? [...(item.fields || []), newField] : item.fields,
    }))
    onChange?.({ ...value, items: updatedItems })
  }, [activeItem?.value, value, onChange])

  const onAddNewItem = useCallback(async () => {
    const fileId = await filePickModal.open({})
    if (!fileId || !selectedArea) {
      return
    }
    handleSelectArea(selectedArea.id)
    const itemId = generateId()
    if (fileId) {
      const newItem = createNewItem({ fileId, itemId })
      onChange?.({
        ...value,
        items: [
          ...value.items.map((item) => {
            return item.value === (state?.active || activeItem.value)
              ? {
                  ...item,
                  areas: areas?.map((area) =>
                    area.id === selectedAreaId ? { ...area, itemId } : area,
                  ),
                }
              : item
          }),
          newItem,
        ],
      })
    }
  }, [
    value,
    filePickModal,
    onChange,
    state?.active,
    selectedArea,
    activeItem.value,
    areas,
    selectedAreaId,
    handleSelectArea,
  ])

  const onLinkArea = useCallback(
    (itemId: string) => {
      if (selectedArea) {
        updateAreaInValue({ ...selectedArea, itemId })
      }
    },
    [selectedArea, updateAreaInValue],
  )

  const onDeleteArea = useCallback(
    (areaId: string) => {
      onChange?.({
        ...value,
        items: [
          ...value.items.map((item) => {
            return item.value === activeItem.value
              ? {
                  ...item,
                  areas: areas?.filter((area) => area.id !== areaId),
                }
              : item
          }),
        ],
      })
    },
    [value, activeItem.value, areas, onChange],
  )

  const onDeleteField = useCallback(
    (fieldId: string) => {
      onChange?.({
        ...value,
        items: [
          ...value.items.map((item) => {
            return item.value === activeItem.value
              ? {
                  ...item,
                  fields: fields?.filter((field) => field.id !== fieldId),
                }
              : item
          }),
        ],
      })
      clearFieldSelection()
    },
    [value, activeItem.value, fields, onChange, clearFieldSelection],
  )

  return {
    filePickModal,
    onAddField,
    onAddNewItem,
    onLinkArea,
    onDeleteArea,
    onDeleteField,
  }
}
